import { analyseSpectrum } from './outputAnalysis'
import type { EchoPlayback, HandVoice, InstrumentControl, LooperPort, LoopLayerState, NoteEvent, ThereminAudioPort, VoicePreset } from '../domain/types'
import { PhraseLooperState } from './looperState'
import { SAMPLE_VOICES, isSampledVoice, nearestSample, samplePath, type SampleNote } from './sampleMap'

export interface ThereminSystem {
  audio: ThereminAudioPort
  looper: LooperPort
}

interface SystemCallbacks {
  onLayers(layers: readonly LoopLayerState[], feedback: string): void
  // Fired whenever the browser starts or stops letting the context run.
  onAudioState?(running: boolean): void
}

interface OscSpec {
  type: OscillatorType
  ratio: number
  detune: number
  gain: number
}

interface VoiceConfig {
  oscs: OscSpec[]
  vibratoCents: number
  breath: number
  cutoffBase: number
  cutoffRange: number
  level: number
  glideMs: number
}

const OSC_COUNT = 5
const CHANNEL_COUNT = 2

const SILENT_OSCS: OscSpec[] = Array.from({ length: OSC_COUNT }, () => ({ type: 'sine', ratio: 1, detune: 0, gain: 0 }))

const VOICES: Record<VoicePreset, VoiceConfig> = {
  harp: { oscs: SILENT_OSCS, vibratoCents: 0, breath: 0, cutoffBase: 2_400, cutoffRange: 9_000, level: 1, glideMs: 0 },
  'music-box': { oscs: SILENT_OSCS, vibratoCents: 0, breath: 0, cutoffBase: 3_000, cutoffRange: 9_000, level: 1.35, glideMs: 0 },
  piano: { oscs: SILENT_OSCS, vibratoCents: 0, breath: 0, cutoffBase: 1_800, cutoffRange: 9_000, level: 1, glideMs: 0 },
  'glass-bow': {
    oscs: [
      { type: 'sine', ratio: 1, detune: 0, gain: 0.5 },
      { type: 'triangle', ratio: 1, detune: 6, gain: 0.34 },
      { type: 'sine', ratio: 2, detune: -5, gain: 0.17 },
      { type: 'sine', ratio: 0.5, detune: 0, gain: 0.2 },
      { type: 'sine', ratio: 3, detune: 9, gain: 0.05 }
    ],
    vibratoCents: 6.5, breath: 0.006, cutoffBase: 1_200, cutoffRange: 6_800, level: 0.85, glideMs: 80
  },
  'breath-choir': {
    oscs: [
      { type: 'triangle', ratio: 1, detune: -5, gain: 0.36 },
      { type: 'triangle', ratio: 1, detune: 5, gain: 0.36 },
      { type: 'sine', ratio: 2, detune: 9, gain: 0.12 },
      { type: 'sine', ratio: 0.5, detune: 0, gain: 0.16 },
      { type: 'sine', ratio: 4, detune: 0, gain: 0.03 }
    ],
    vibratoCents: 5.5, breath: 0.03, cutoffBase: 900, cutoffRange: 4_600, level: 1.5, glideMs: 95
  },
  aurora: {
    oscs: [
      { type: 'sine', ratio: 1, detune: 0, gain: 0.6 },
      { type: 'sine', ratio: 1, detune: 5, gain: 0.26 },
      { type: 'sine', ratio: 3, detune: 7, gain: 0.05 },
      { type: 'sine', ratio: 0.5, detune: 0, gain: 0.2 },
      { type: 'sine', ratio: 6, detune: -8, gain: 0.012 }
    ],
    vibratoCents: 9, breath: 0.004, cutoffBase: 1_500, cutoffRange: 7_000, level: 1.2, glideMs: 170
  }
}

export function createThereminSystem(silent: boolean, callbacks: SystemCallbacks): ThereminSystem {
  return silent ? createSilentSystem(callbacks) : new BrowserThereminSystem(callbacks).ports()
}

interface SynthOsc {
  osc: OscillatorNode
  gain: GainNode
}

interface VoiceChannel {
  oscs: SynthOsc[]
  filter: BiquadFilterNode
  gain: GainNode
  panner: StereoPannerNode
  lastMidi: number
}

class BrowserThereminSystem implements ThereminAudioPort, LooperPort {
  private context: AudioContext | null = null
  private channels: VoiceChannel[] = []
  private noise: AudioBufferSourceNode | null = null
  private noiseGain: GainNode | null = null
  private delaySend: GainNode | null = null
  private reverbSend: GainNode | null = null
  private masterLevelNode: GainNode | null = null
  private uiBus: GainNode | null = null
  private echoBus: GainNode | null = null
  private droneGain: GainNode | null = null
  private looperNode: AudioWorkletNode | null = null
  private captureDestination: MediaStreamAudioDestinationNode | null = null
  private layers: LoopLayerState[] = createEmptyLayers()
  private preset: VoicePreset = 'harp'
  private masterLevel = 0.78
  private lastControl: InstrumentControl | null = null
  private vibratoDepth: GainNode | null = null
  private driftDepth: GainNode | null = null
  private muteNode: GainNode | null = null
  private isMuted = false
  private levelAnalyser: AnalyserNode | null = null
  private levelSamples = new Float32Array(0)
  private spectrumSamples = new Float32Array(0)
  private presetGeneration = 0
  private sampleBytes = new Map<string, ArrayBuffer>()
  private sampleBuffers = new Map<VoicePreset, Map<string, AudioBuffer>>()
  private sampleLoads = new Map<VoicePreset, Promise<void>>()
  private unlocked = false
  private lifecycle: Promise<void> = Promise.resolve()
  private disposed = false

  constructor(private readonly callbacks: SystemCallbacks) {}

  ports(): ThereminSystem { return { audio: this, looper: this } }
  get state(): readonly LoopLayerState[] { return this.layers }
  get currentTime(): number { return this.context?.currentTime ?? performance.now() / 1_000 }
  get running(): boolean { return this.context?.state === 'running' }
  get muted(): boolean { return this.isMuted }

  preload(): Promise<void> { return this.fetchSampleBytes(this.preset) }

  // Builds the graph on the first call and asks the context to run. The graph
  // is built even while the browser still holds the context suspended (it only
  // wants a user gesture before sound flows), so this never hangs: it resolves
  // once the engine exists, and `running` reports whether sound is audible.
  // Call it again from any user gesture to lift a suspended context.
  unlock(): Promise<void> { return this.enqueue(() => this.initialize()) }

  private async initialize(): Promise<void> {
    if (this.disposed) throw new Error('The audio engine has been disposed.')
    if (this.context) {
      await this.tryResume(this.context)
      return
    }
    const context = new AudioContext({ latencyHint: 'interactive' })
    context.addEventListener('statechange', () => { if (this.context === context) this.callbacks.onAudioState?.(context.state === 'running') })
    let captureDestination: MediaStreamAudioDestinationNode | null = null
    const sources: (AudioBufferSourceNode | OscillatorNode)[] = []
    try {
      void this.tryResume(context)
      await Promise.all([context.audioWorklet.addModule('/looper-worklet.js'), this.fetchSampleBytes(this.preset)])
      if (this.disposed) throw new Error('Audio initialization was canceled.')

      const config = VOICES[this.preset]

      const vibrato = context.createOscillator()
      vibrato.type = 'sine'
      vibrato.frequency.value = 5.1
      const vibratoDepth = context.createGain()
      vibratoDepth.gain.value = config.vibratoCents
      vibrato.connect(vibratoDepth)
      vibrato.start()
      sources.push(vibrato)

      const drift = context.createOscillator()
      drift.type = 'sine'
      drift.frequency.value = 0.19
      const driftDepth = context.createGain()
      driftDepth.gain.value = 2.4
      drift.connect(driftDepth)
      drift.start()
      sources.push(drift)

      // Mono sum of both hand voices; this is what the phrase looper records.
      const looperFeed = context.createGain()
      looperFeed.gain.value = 1

      const performanceSum = context.createGain()
      performanceSum.gain.value = 0.86

      const shimmer = context.createOscillator()
      shimmer.type = 'sine'
      shimmer.frequency.value = 0.31
      const shimmerDepth = context.createGain()
      shimmerDepth.gain.value = 0.03
      shimmer.start()
      sources.push(shimmer)

      const channels: VoiceChannel[] = Array.from({ length: CHANNEL_COUNT }, () => {
        const mix = context.createGain()
        mix.gain.value = 1
        shimmerDepth.connect(mix.gain)
        const oscs: SynthOsc[] = Array.from({ length: OSC_COUNT }, (_, index) => {
          const spec = config.oscs[index]!
          const osc = context.createOscillator()
          osc.type = spec.type
          osc.frequency.value = 261.63 * spec.ratio
          osc.detune.value = spec.detune
          vibratoDepth.connect(osc.detune)
          driftDepth.connect(osc.detune)
          const gain = context.createGain()
          gain.gain.value = spec.gain
          osc.connect(gain).connect(mix)
          osc.start()
          sources.push(osc)
          return { osc, gain }
        })
        const filter = context.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.value = 3_500
        filter.Q.value = 0.72
        const gain = context.createGain()
        gain.gain.value = 0
        const panner = context.createStereoPanner()
        panner.pan.value = 0
        mix.connect(filter).connect(gain)
        gain.connect(looperFeed)
        gain.connect(panner).connect(performanceSum)
        return { oscs, filter, gain, panner, lastMidi: 60 }
      })
      shimmer.connect(shimmerDepth)

      const noise = context.createBufferSource()
      noise.buffer = createNoiseBuffer(context)
      noise.loop = true
      const noiseFilter = context.createBiquadFilter()
      noiseFilter.type = 'bandpass'
      noiseFilter.frequency.value = 1_700
      noiseFilter.Q.value = 0.8
      const noiseGain = context.createGain()
      noiseGain.gain.value = 0
      noise.connect(noiseFilter).connect(noiseGain)
      noiseGain.connect(looperFeed)
      noiseGain.connect(performanceSum)
      noise.start()
      sources.push(noise)

      // Root drone: a soft C2/C3 bed that swells in while anything plays so a
      // single note already sits inside a key. Kept out of the looper feed.
      const droneGain = context.createGain()
      droneGain.gain.value = 0
      const droneFilter = context.createBiquadFilter()
      droneFilter.type = 'lowpass'
      droneFilter.frequency.value = 420
      for (const [ratio, type, level] of [[0.5, 'sine', 0.7], [1, 'triangle', 0.35], [1.5, 'sine', 0.12]] as [number, OscillatorType, number][]) {
        const osc = context.createOscillator()
        osc.type = type
        osc.frequency.value = 130.81 * ratio
        driftDepth.connect(osc.detune)
        const gain = context.createGain()
        gain.gain.value = level
        osc.connect(gain).connect(droneFilter)
        osc.start()
        sources.push(osc)
      }
      droneFilter.connect(droneGain).connect(performanceSum)

      const looperNode = new AudioWorkletNode(context, 'aether-phrase-looper', {
        numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], channelCount: 1, channelCountMode: 'explicit'
      })
      looperFeed.connect(looperNode)
      looperNode.connect(performanceSum)

      const masterBus = context.createGain()
      const dry = context.createGain()
      dry.gain.value = 0.78
      performanceSum.connect(dry).connect(masterBus)

      const chorusLfo = context.createOscillator()
      chorusLfo.type = 'sine'
      chorusLfo.frequency.value = 0.55
      chorusLfo.start()
      sources.push(chorusLfo)
      const chorusTaps: [number, number, number][] = [[0.017, 0.0028, -0.72], [0.024, -0.0032, 0.72]]
      for (const [base, depth, pan] of chorusTaps) {
        const tap = context.createDelay(0.08)
        tap.delayTime.value = base
        const depthGain = context.createGain()
        depthGain.gain.value = depth
        chorusLfo.connect(depthGain).connect(tap.delayTime)
        const tapGain = context.createGain()
        tapGain.gain.value = 0.3
        const panner = context.createStereoPanner()
        panner.pan.value = pan
        performanceSum.connect(tap).connect(tapGain).connect(panner).connect(masterBus)
      }

      const delay = context.createDelay(0.8)
      delay.delayTime.value = 0.34
      const delaySend = context.createGain()
      delaySend.gain.value = 0.07
      const feedback = context.createGain()
      feedback.gain.value = 0.24
      const feedbackTone = context.createBiquadFilter()
      feedbackTone.type = 'lowpass'
      feedbackTone.frequency.value = 2_400
      performanceSum.connect(delaySend).connect(delay)
      delay.connect(feedbackTone).connect(feedback).connect(delay)
      delay.connect(masterBus)

      const convolver = context.createConvolver()
      convolver.buffer = createImpulse(context, 3.2)
      const reverbSend = context.createGain()
      reverbSend.gain.value = 0.12
      performanceSum.connect(reverbSend).connect(convolver).connect(masterBus)

      // Echo garden repeats: dry into the performance path (not the looper) and
      // deep into the reverb so every generation drifts further away.
      const echoBus = context.createGain()
      echoBus.gain.value = 1
      echoBus.connect(performanceSum)
      const echoReverb = context.createGain()
      echoReverb.gain.value = 0.42
      echoBus.connect(echoReverb).connect(convolver)

      const uiBus = context.createGain()
      uiBus.gain.value = 1
      uiBus.connect(masterBus)
      const uiSparkle = context.createGain()
      uiSparkle.gain.value = 0.4
      uiBus.connect(uiSparkle).connect(convolver)

      const compressor = context.createDynamicsCompressor()
      compressor.threshold.value = -18
      compressor.knee.value = 18
      compressor.ratio.value = 3.2
      compressor.attack.value = 0.009
      compressor.release.value = 0.22
      const softClip = context.createWaveShaper()
      softClip.curve = saturationCurve(1.18)
      softClip.oversample = '2x'
      const masterLevelNode = context.createGain()
      masterLevelNode.gain.value = this.masterLevel
      // "Sound off" mutes here, after the level control, so the slider never unmutes.
      const muteNode = context.createGain()
      muteNode.gain.value = this.isMuted ? 0 : 1
      const ceiling = context.createGain()
      ceiling.gain.value = 0.89
      masterBus.connect(compressor).connect(softClip).connect(masterLevelNode).connect(muteNode).connect(ceiling)
      ceiling.connect(context.destination)

      if ('createMediaStreamDestination' in context) {
        captureDestination = context.createMediaStreamDestination()
        ceiling.connect(captureDestination)
      }

      attachProbe(context, ceiling)
      const levelAnalyser = context.createAnalyser()
      levelAnalyser.fftSize = 4096
      levelAnalyser.smoothingTimeConstant = .55
      ceiling.connect(levelAnalyser)
      this.levelAnalyser = levelAnalyser
      this.levelSamples = new Float32Array(levelAnalyser.fftSize)
      this.spectrumSamples = new Float32Array(levelAnalyser.frequencyBinCount)

      looperNode.port.onmessage = ({ data }) => {
        if (data?.type !== 'state' || !Array.isArray(data.layers)) return
        this.layers = data.layers.map((layer: Partial<LoopLayerState>, index: number) => normalizeLayer(layer, index, this.layers[index]?.waveformPeaks))
        this.callbacks.onLayers(this.layers, String(data.feedback ?? ''))
      }

      if (this.disposed) throw new Error('Audio initialization was canceled.')
      this.context = context
      this.channels = channels
      this.noise = noise
      this.noiseGain = noiseGain
      this.delaySend = delaySend
      this.reverbSend = reverbSend
      this.masterLevelNode = masterLevelNode
      this.muteNode = muteNode
      this.uiBus = uiBus
      this.echoBus = echoBus
      this.droneGain = droneGain
      this.vibratoDepth = vibratoDepth
      this.driftDepth = driftDepth
      this.looperNode = looperNode
      this.captureDestination = captureDestination
      this.unlocked = true
      await this.decodeSamples(this.preset)
      if (this.lastControl) this.update({ ...this.lastControl, events: [] })
      await this.tryResume(context)
      this.callbacks.onAudioState?.(context.state === 'running')
    } catch (error) {
      sources.forEach((source) => { try { source.stop() } catch { /* already stopped */ } })
      captureDestination?.stream.getTracks().forEach((track) => track.stop())
      try { await context.close() } catch { /* the context may already be closing */ }
      this.context = null
      this.captureDestination = null
      this.unlocked = false
      throw error
    }
  }

  // A browser that wants a user gesture first leaves resume() pending forever;
  // wait briefly, then report the real state instead of blocking the engine.
  private async tryResume(context: AudioContext): Promise<boolean> {
    if (context.state === 'running') return true
    if (context.state === 'closed') return false
    await Promise.race([context.resume().catch(() => undefined), new Promise<void>((resolve) => setTimeout(resolve, 350))])
    // Re-read after the await: the state may have changed underneath the narrowing.
    return (context.state as AudioContextState) === 'running'
  }

  update(control: InstrumentControl): void {
    this.lastControl = control
    if (!this.unlocked || !this.context) return
    const now = this.context.currentTime
    const config = VOICES[this.preset]
    const sampled = isSampledVoice(this.preset)
    let breathiestTimbre = 0
    let anyGate = false
    control.voices.forEach((voice, index) => {
      const channel = this.channels[index]
      if (!channel) return
      this.updateChannel(channel, voice, config, sampled, now)
      if (voice.gate) { anyGate = true; breathiestTimbre = Math.max(breathiestTimbre, voice.timbre) }
    })
    // Several note events in one frame are a sweep: strum them a few ms apart.
    if (sampled) control.events.forEach((event, index) => this.handleNoteEvent(event, now + index * 0.024))
    this.noiseGain?.gain.cancelScheduledValues(now)
    this.noiseGain?.gain.setTargetAtTime(anyGate ? config.breath * (0.45 + breathiestTimbre * 0.55) : 0, now, 0.05)
    this.droneGain?.gain.cancelScheduledValues(now)
    this.droneGain?.gain.setTargetAtTime(anyGate ? 0.05 : 0, now, anyGate ? 0.7 : 1.6)
    this.delaySend?.gain.cancelScheduledValues(now)
    this.delaySend?.gain.setTargetAtTime(0.02 + control.ambience * 0.12, now, 0.08)
    this.reverbSend?.gain.cancelScheduledValues(now)
    this.reverbSend?.gain.setTargetAtTime(0.05 + control.ambience * 0.2, now, 0.08)
  }

  private updateChannel(channel: VoiceChannel, voice: HandVoice, config: VoiceConfig, sampled: boolean, now: number): void {
    if (voice.active) {
      const targetHz = Math.max(55, Math.min(2_100, voice.pitchHz))
      const distance = Math.abs(channel.lastMidi - voice.targetMidi)
      channel.lastMidi = voice.targetMidi
      const glideTau = Math.max(0.008, config.glideMs / 1_000 / 3) * (0.6 + 0.4 * Math.min(1, distance / 12))
      channel.oscs.forEach((entry, index) => {
        const spec = config.oscs[index]!
        entry.osc.frequency.cancelScheduledValues(now)
        entry.osc.frequency.setTargetAtTime(targetHz * spec.ratio, now, glideTau)
      })
      channel.filter.frequency.cancelScheduledValues(now)
      channel.filter.frequency.setTargetAtTime(config.cutoffBase + voice.timbre * config.cutoffRange, now, 0.05)
      channel.panner.pan.cancelScheduledValues(now)
      channel.panner.pan.setTargetAtTime(voice.pan, now, 0.08)
    }
    // Sampled voices carry their own envelopes, so the channel stays open and
    // plucked tails keep ringing after the hand closes.
    const gain = sampled ? config.level : voice.gate ? voice.level * 0.3 * config.level : 0
    channel.gain.gain.cancelScheduledValues(now)
    channel.gain.gain.setTargetAtTime(gain, now, sampled ? 0.03 : voice.gate ? 0.02 : 0.12)
  }

  private handleNoteEvent(event: NoteEvent, now: number): void {
    if (event.type === 'off') return
    const channel = this.channels[event.slot]
    if (!channel) return
    this.pluck(this.preset, event.midi, event.velocity, now, channel.filter, null)
  }

  private pluck(preset: VoicePreset, midi: number, velocity: number, when: number, destination: AudioNode, tone: BiquadFilterNode | null): boolean {
    const voice = SAMPLE_VOICES[preset]
    const buffers = this.sampleBuffers.get(preset)
    if (!this.context || !voice || !buffers) return false
    const { note, rate } = nearestSample(midi, voice.notes)
    const buffer = buffers.get(note.name)
    if (!buffer) return false
    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.playbackRate.value = rate
    const gain = this.context.createGain()
    const peak = 0.85 * Math.pow(Math.max(0.05, velocity), 1.3)
    gain.gain.setValueAtTime(0.0001, when)
    gain.gain.exponentialRampToValueAtTime(peak, when + 0.004)
    const tail = Math.min(buffer.duration / rate, 7)
    gain.gain.setTargetAtTime(0.0001, when + Math.max(0.05, tail - 0.6), 0.18)
    source.connect(gain)
    if (tone) gain.connect(tone).connect(destination)
    else gain.connect(destination)
    source.start(when)
    source.stop(when + tail + 0.1)
    source.onended = () => { source.disconnect(); gain.disconnect(); tone?.disconnect() }
    return true
  }

  // An echo repeats in the voice the note was played with (the seed carries
  // it), whatever voice is selected by the time it fires: sampled voices repeat
  // their own sample, synth voices a short note from their own oscillator bank.
  playEcho(echo: EchoPlayback): (() => void) | void {
    if (!this.context || !this.echoBus) return
    const preset = echo.preset ?? this.preset
    const when = Math.max(this.context.currentTime, echo.when)
    const panner = this.context.createStereoPanner()
    panner.pan.value = echo.pan
    panner.connect(this.echoBus)
    const tone = this.context.createBiquadFilter()
    tone.type = 'lowpass'
    tone.frequency.value = 5_200 * 0.62 ** echo.generation
    if (isSampledVoice(preset)) {
      // Never substitute another instrument: a sample that is still decoding is skipped.
      if (!this.pluck(preset, echo.midi, echo.gain, when, panner, tone)) { panner.disconnect(); return }
    } else {
      this.synthNote(preset, echo.midi, echo.gain, echo.timbre, when, tone, panner)
    }
    return () => panner.disconnect()
  }

  // One short note in a sustained synth voice: the preset's own oscillator
  // recipe, vibrato and filter, with a soft swell and a ringing decay.
  private synthNote(preset: VoicePreset, midi: number, gain: number, timbre: number, when: number, tone: BiquadFilterNode, destination: AudioNode): void {
    if (!this.context) return
    const config = VOICES[preset]
    const hz = 440 * 2 ** ((midi - 69) / 12)
    const attack = 0.02 + config.glideMs / 1_000 * 0.35
    const envelope = this.context.createGain()
    envelope.gain.setValueAtTime(0.0001, when)
    envelope.gain.exponentialRampToValueAtTime(0.26 * config.level * Math.max(0.05, gain), when + attack)
    envelope.gain.exponentialRampToValueAtTime(0.0001, when + 1.7)
    const filter = this.context.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = config.cutoffBase + clamp01(timbre) * config.cutoffRange
    filter.Q.value = 0.72
    for (const spec of config.oscs) {
      if (spec.gain <= 0) continue
      const osc = this.context.createOscillator()
      osc.type = spec.type
      osc.frequency.value = hz * spec.ratio
      osc.detune.value = spec.detune
      this.vibratoDepth?.connect(osc.detune)
      this.driftDepth?.connect(osc.detune)
      const partial = this.context.createGain()
      partial.gain.value = spec.gain
      osc.connect(partial).connect(envelope)
      osc.start(when)
      osc.stop(when + 1.8)
      osc.onended = () => { this.vibratoDepth?.disconnect(osc.detune); this.driftDepth?.disconnect(osc.detune); osc.disconnect(); partial.disconnect() }
    }
    envelope.connect(filter).connect(tone).connect(destination)
  }

  hush(): void {
    if (!this.context || !this.echoBus || !this.uiBus) return
    const now = this.context.currentTime
    this.echoBus.gain.cancelScheduledValues(now)
    this.echoBus.gain.setTargetAtTime(0, now, 0.12)
    this.echoBus.gain.setTargetAtTime(1, now + 0.7, 0.2)
    // A soft falling "shh" so clearing the garden is heard as well as seen.
    const oscillator = this.context.createOscillator()
    const gain = this.context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(660, now)
    oscillator.frequency.exponentialRampToValueAtTime(330, now + 0.32)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.36)
    oscillator.connect(gain).connect(this.uiBus)
    oscillator.start(now)
    oscillator.stop(now + 0.4)
  }

  analyseOutput(midiNotes: readonly number[]) {
    if (!this.levelAnalyser || this.context?.state !== 'running') return { level: 0, brightness: 0, notes: midiNotes.map(() => 0) }
    this.levelAnalyser.getFloatFrequencyData(this.spectrumSamples)
    return analyseSpectrum(this.outputLevel(), this.spectrumSamples, this.context.sampleRate, midiNotes)
  }

  outputLevel(): number {
    if (!this.levelAnalyser || this.context?.state !== 'running') return 0
    this.levelAnalyser.getFloatTimeDomainData(this.levelSamples)
    let sum = 0
    for (let index = 0; index < this.levelSamples.length; index += 1) sum += this.levelSamples[index]! * this.levelSamples[index]!
    return Math.sqrt(sum / this.levelSamples.length)
  }

  async setPreset(preset: VoicePreset): Promise<void> {
    const previous = this.preset
    this.preset = preset
    if (isSampledVoice(preset)) {
      await this.fetchSampleBytes(preset)
      if (this.context) await this.decodeSamples(preset)
    }
    if (!this.context || this.channels.length === 0 || previous === preset) {
      if (this.lastControl) this.update({ ...this.lastControl, events: [] })
      return
    }
    const generation = ++this.presetGeneration
    const context = this.context
    const fadeOutAt = context.currentTime
    for (const channel of this.channels) {
      for (const entry of channel.oscs) {
        entry.gain.gain.cancelScheduledValues(fadeOutAt)
        entry.gain.gain.setTargetAtTime(0, fadeOutAt, 0.016)
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 70))
    if (this.disposed || generation !== this.presetGeneration || !this.context) return
    const config = VOICES[this.preset]
    const now = this.context.currentTime
    this.vibratoDepth?.gain.setTargetAtTime(config.vibratoCents, now, 0.1)
    for (const channel of this.channels) {
      channel.oscs.forEach((entry, index) => {
        const spec = config.oscs[index]!
        entry.osc.type = spec.type
        entry.osc.detune.value = spec.detune
        entry.gain.gain.cancelScheduledValues(now)
        entry.gain.gain.setTargetAtTime(spec.gain, now, 0.04)
      })
    }
    // Re-apply the held control without its note events: those already sounded.
    if (this.lastControl) this.update({ ...this.lastControl, events: [] })
  }

  private fetchSampleBytes(preset: VoicePreset): Promise<void> {
    const voice = SAMPLE_VOICES[preset]
    if (!voice) return Promise.resolve()
    const pending = this.sampleLoads.get(preset)
    if (pending) return pending
    const load = Promise.all(voice.notes.map(async (note: SampleNote) => {
      const path = samplePath(voice, note)
      if (this.sampleBytes.has(path)) return
      const response = await fetch(path)
      if (!response.ok) throw new Error(`Could not load the ${preset} sound (${note.name}).`)
      this.sampleBytes.set(path, await response.arrayBuffer())
    })).then(() => undefined).catch((error: unknown) => {
      this.sampleLoads.delete(preset)
      throw error
    })
    this.sampleLoads.set(preset, load)
    return load
  }

  private async decodeSamples(preset: VoicePreset): Promise<void> {
    const voice = SAMPLE_VOICES[preset]
    const context = this.context
    if (!voice || !context || this.sampleBuffers.has(preset)) return
    const decoded = new Map<string, AudioBuffer>()
    await Promise.all(voice.notes.map(async (note) => {
      const bytes = this.sampleBytes.get(samplePath(voice, note))
      if (!bytes) return
      decoded.set(note.name, await context.decodeAudioData(bytes.slice(0)))
    }))
    this.sampleBuffers.set(preset, decoded)
  }

  setMasterLevel(level: number): void {
    this.masterLevel = clamp01(level)
    if (!this.context || !this.masterLevelNode) return
    const now = this.context.currentTime
    this.masterLevelNode.gain.cancelScheduledValues(now)
    this.masterLevelNode.gain.linearRampToValueAtTime(this.masterLevel, now + 0.06)
  }

  // "Sound off": silences the output while the clocks keep running, so echoes
  // and loops stay in time and come straight back on unmute.
  setMuted(muted: boolean): void {
    this.isMuted = muted
    if (!this.context || !this.muteNode) return
    const now = this.context.currentTime
    this.muteNode.gain.cancelScheduledValues(now)
    this.muteNode.gain.setTargetAtTime(muted ? 0 : 1, now, 0.03)
  }

  playUiCue(): void {
    if (!this.context || !this.uiBus) return
    const now = this.context.currentTime
    const notes: [number, number][] = [[1_046.5, 0], [1_568, 0.045]]
    for (const [frequency, delay] of notes) {
      const oscillator = this.context.createOscillator()
      const gain = this.context.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(frequency, now + delay)
      gain.gain.setValueAtTime(0.0001, now + delay)
      gain.gain.exponentialRampToValueAtTime(0.04, now + delay + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.14)
      oscillator.connect(gain).connect(this.uiBus)
      oscillator.start(now + delay)
      oscillator.stop(now + delay + 0.16)
    }
  }

  release(fadeSeconds = 0.1): void {
    if (!this.context) return
    const now = this.context.currentTime
    for (const channel of this.channels) {
      channel.gain.gain.cancelScheduledValues(now)
      channel.gain.gain.setTargetAtTime(0, now, Math.max(0.01, fadeSeconds))
    }
    this.noiseGain?.gain.cancelScheduledValues(now)
    this.noiseGain?.gain.setTargetAtTime(0, now, Math.max(0.01, fadeSeconds))
    this.droneGain?.gain.cancelScheduledValues(now)
    this.droneGain?.gain.setTargetAtTime(0, now, Math.max(0.05, fadeSeconds))
  }

  outputStream(): MediaStream | null { return this.captureDestination?.stream ?? null }

  suspend(): Promise<void> {
    return this.enqueue(async () => {
      this.release()
      if (this.context?.state === 'running') await this.context.suspend()
    })
  }

  tap(): void { this.post({ type: 'tap' }) }
  toggleMute(layer: number): void { this.post({ type: 'toggleMute', layer }) }
  clear(layer: number): void { this.post({ type: 'clear', layer }) }
  clearAll(): void { this.post({ type: 'clearAll' }) }
  undo(): void { this.post({ type: 'undo' }) }
  setPlaying(playing: boolean): void { this.post({ type: 'setPlaying', playing }) }

  dispose(): void {
    this.disposed = true
    this.release(0.01)
    this.looperNode?.disconnect()
    for (const channel of this.channels) {
      for (const entry of channel.oscs) { try { entry.osc.stop() } catch { /* already stopped */ } }
    }
    try { this.noise?.stop() } catch { /* already stopped */ }
    this.captureDestination?.stream.getTracks().forEach((track) => track.stop())
    void this.context?.close()
    this.context = null
    this.unlocked = false
  }

  private post(command: Record<string, unknown>): void { this.looperNode?.port.postMessage(command) }

  private enqueue(operation: () => Promise<void>): Promise<void> {
    const run = this.lifecycle.then(operation, operation)
    this.lifecycle = run.catch(() => undefined)
    return run
  }
}

function attachProbe(context: AudioContext, tail: AudioNode): void {
  try {
    if (!import.meta.env.DEV || new URLSearchParams(window.location.search).get('probe') !== '1') return
    const analyser = context.createAnalyser()
    // A short window so onset measurements are not smeared by the analysis length.
    analyser.fftSize = 512
    tail.connect(analyser)
    const samples = new Float32Array(analyser.fftSize)
    ;(window as Window & { __aetherMeter?: () => { rms: number; peak: number; state: string; time: number } }).__aetherMeter = () => {
      analyser.getFloatTimeDomainData(samples)
      let sum = 0
      let peak = 0
      for (const value of samples) { sum += value * value; peak = Math.max(peak, Math.abs(value)) }
      return { rms: Math.sqrt(sum / samples.length), peak, state: context.state, time: context.currentTime }
    }
  } catch { /* the probe is a dev-only convenience */ }
}

function createSilentSystem(callbacks: SystemCallbacks): ThereminSystem {
  const model = new PhraseLooperState({ sampleRate: 48_000 })
  let layers = model.snapshot().layers
  const publish = (feedback: string): void => { layers = model.snapshot().layers; callbacks.onLayers(layers, feedback) }
  let lastTickMs = performance.now()
  const clock = window.setInterval(() => {
    const timestampMs = performance.now()
    model.advance(timestampMs - lastTickMs)
    lastTickMs = timestampMs
    publish('tick')
  }, 33)
  const audio: ThereminAudioPort = {
    async preload(): Promise<void> {}, async unlock(): Promise<void> {}, update(): void {}, async setPreset(): Promise<void> {},
    setMasterLevel(): void {}, setMuted(): void {}, running: true, muted: false, playEcho(): void {}, hush(): void {}, outputLevel: () => 0, get currentTime(): number { return performance.now() / 1_000 },
    playUiCue(): void {}, release(): void {}, outputStream: () => null,
    async suspend(): Promise<void> {}, dispose(): void {}
  }
  const looper: LooperPort = {
    get state(): readonly LoopLayerState[] { return layers },
    tap(timestampMs): void { const result = model.tap(timestampMs); publish(result.kind) },
    toggleMute(layer): void { model.toggleMute(layer); publish('toggleMute') },
    clear(layer): void { model.clear(layer); publish('clear') }, clearAll(): void { model.clearAll(); publish('clearAll') },
    undo(): void { model.undo(); publish('undo') }, setPlaying(playing): void { model.setPlaying(playing); publish('setPlaying') },
    dispose(): void { window.clearInterval(clock) }
  }
  return { audio, looper }
}

function createEmptyLayers(): LoopLayerState[] {
  return Array.from({ length: 4 }, (_, index) => ({ index, status: 'empty', loopPhase: 0, durationSeconds: 0, gain: 1, waveformPeaks: [], recordStartPhase: 0 }))
}

function normalizeLayer(layer: Partial<LoopLayerState>, index: number, previousPeaks: number[] = []): LoopLayerState {
  return { index, status: layer.status ?? 'empty', loopPhase: Number(layer.loopPhase ?? 0), durationSeconds: Number(layer.durationSeconds ?? 0), gain: Number(layer.gain ?? 1), waveformPeaks: Array.isArray(layer.waveformPeaks) && layer.waveformPeaks.length > 0 ? layer.waveformPeaks.map(Number) : [...previousPeaks], recordStartPhase: Number(layer.recordStartPhase ?? 0) }
}

function createNoiseBuffer(context: AudioContext): AudioBuffer {
  const buffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate)
  const data = buffer.getChannelData(0)
  let value = 0
  for (let index = 0; index < data.length; index += 1) { value = value * 0.965 + (Math.random() * 2 - 1) * 0.035; data[index] = value }
  return buffer
}

function createImpulse(context: AudioContext, seconds: number): AudioBuffer {
  const predelay = Math.round(context.sampleRate * 0.02)
  const length = Math.ceil(context.sampleRate * seconds)
  const buffer = context.createBuffer(2, predelay + length, context.sampleRate)
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel)
    let lowpass = 0
    let peak = 0
    for (let index = 0; index < length; index += 1) {
      const progress = index / length
      const smoothing = Math.max(0.03, 0.28 - 0.24 * progress)
      lowpass += ((Math.random() * 2 - 1) - lowpass) * smoothing
      data[predelay + index] = lowpass * (1 - progress) ** 2.6
      peak = Math.max(peak, Math.abs(data[predelay + index]!))
    }
    if (peak > 0) for (let index = predelay; index < data.length; index += 1) data[index]! /= peak * 2.4
  }
  return buffer
}

function saturationCurve(drive: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(2_048)
  const normalizer = Math.tanh(drive)
  for (let index = 0; index < curve.length; index += 1) {
    const x = index * 2 / (curve.length - 1) - 1
    curve[index] = Math.tanh(x * drive) / normalizer
  }
  return curve
}

function clamp01(value: number): number { return Math.max(0, Math.min(1, value)) }
